import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import styled from "styled-components";
import axios from "axios";
import KPIBubbles from "../components/KPIBubbles";
import DataVisualizer from "../components/DataVisualizer";
import CreativeGallery from "../components/CreativeGallery";
import Recommendations from "../components/Recommendations";
import logo from "./logo.png";

const DashboardContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;
`;

const HeaderContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 40px;
  padding-top: 20px;
`;

const Logo = styled.img`
  position: absolute;
  left: 20px;
  top: 20px;
  height: 60px;
`;

const CampaignTitle = styled.h1`
  color: #333;
  text-align: center;
  text-align: center;
  font-size: 32px;
  margin: 0;
`;

const HeaderActions = styled.div`
  position: absolute;
  right: 20px;
  top: 20px;
  display: flex;
  gap: 10px;
`;

const ActionButton = styled.button`
  background-color: #4285f4;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px 12px;
  cursor: pointer;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 5px;

  &:hover {
    background-color: #3367d6;
  }

  &:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
  }
`;

const BackButton = styled(ActionButton)`
  background-color: #f5f5f5;
  color: #333;

  &:hover {
    background-color: #e0e0e0;
  }
`;

const ShareContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 10px;
  gap: 10px;
`;

const ShareInput = styled.input`
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  width: 350px;
  font-size: 14px;
`;

const ShareButton = styled.button`
  background-color: #4285f4;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px 12px;
  cursor: pointer;
  font-size: 14px;

  &:hover {
    background-color: #3367d6;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 300px;
  font-size: 18px;
  color: #666;
`;

const ErrorContainer = styled.div`
  background-color: #ffebee;
  color: #c62828;
  padding: 15px;
  border-radius: 5px;
  text-align: center;
`;

const CampaignInfo = styled.div`
  background-color: white;
  border-radius: 10px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  margin-bottom: 20px;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 20px;
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
`;

const InfoLabel = styled.span`
  font-size: 14px;
  color: #666;
  margin-bottom: 5px;
`;

const InfoValue = styled.span`
  font-size: 16px;
  font-weight: 500;
  color: #333;
`;

const LastUpdated = styled.div`
  text-align: right;
  font-size: 12px;
  color: #666;
  margin-top: 10px;
`;

const FiltersContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  margin-bottom: 20px;
  padding: 15px;
  background-color: #f5f5f5;
  border-radius: 8px;
`;

const FilterSelect = styled.select`
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  min-width: 200px;
`;

const FilterLabel = styled.label`
  display: flex;
  flex-direction: column;
  gap: 5px;
  font-size: 14px;
  color: #666;
`;

function CampaignDashboard() {
  const { dataId } = useParams();
  const navigate = useNavigate();
  const [campaignData, setCampaignData] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [selectedKPI, setSelectedKPI] = useState("impressions");
  const [selectedFilter, setSelectedFilter] = useState("day");
  const [selectedInsertionOrder, setSelectedInsertionOrder] = useState("all");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  const apiUrl =
    process.env.REACT_APP_API_URL || "https://report-campaign.sirdata.io";
  // Générer l'URL partageable basée sur l'ID actuel
  const shareableUrl = window.location.href;

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [campaignResponse] = await Promise.all([
        axios.get(`${apiUrl}/api/campaign/${dataId}`),
        // axios.get(`${apiUrl}/api/recommendations/${dataId}`),
      ]);

      // Adapter la structure si nécessaire
      if (campaignResponse.data && campaignResponse.data.data) {
        // Normaliser les données pour s'assurer que tous les champs nécessaires sont présents
        const normalizedData = campaignResponse.data.data.map((item) => ({
          ...item,
          // S'assurer que creative est défini pour le groupement
          creative:
            item.creativeName ||
            item.creative_name ||
            `Creative ${item.creativeId || item.creative_id || "Unknown"}`,
          // Normaliser les valeurs numériques
          impressions: Number(item.impressions || item.imps || 0),
          clicks: Number(item.clicks || 0),
          // Normaliser le CTR en pourcentage (0-100)
          ctr: Number(item.ctr || 0) * (item.ctr > 1 ? 1 : 100),
          conversions: Number(item.conversions || item.total_convs || 0),
          budget: Number(item.budget || item.cost || 0),
          viewableImpressions: Number(item.viewableImpressions || 0),
          measurableImpressions: Number(
            item.measurableImpressions || item.imps_viewed || 0
          ),
          // S'assurer que viewability est en pourcentage (0-100)
          viewability:
            Number(item.viewability || 0) * (item.viewability > 1 ? 1 : 100),
          // S'assurer que les champs insertion_order sont définis
          insertionOrderId: item.insertionOrderId || null,
          insertionOrderName: item.insertionOrderName || "Inconnu",
        }));

        campaignResponse.data.data = normalizedData;
      }

      setCampaignData(campaignResponse.data);
      // setRecommendations(recommendationsResponse.data.recommendations);
    } catch (err) {
      setError(
        "Erreur lors du chargement des données: " +
          (err.response?.data?.error || err.message)
      );
    } finally {
      setLoading(false);
    }
  }, [dataId, apiUrl]);
  useEffect(() => {
    fetchData();
  }, [dataId, fetchData]);

  // Extraire les insertion orders uniques
  const insertionOrders = useMemo(() => {
    if (!campaignData || !campaignData.data) return [];

    const uniqueOrders = [
      ...new Set(
        campaignData.data
          .filter((item) => item.insertionOrderId && item.insertionOrderName)
          .map((item) =>
            JSON.stringify({
              id: item.insertionOrderId,
              name: item.insertionOrderName,
            })
          )
      ),
    ].map((item) => JSON.parse(item));

    return uniqueOrders;
  }, [campaignData]);

  // Filtrer les données en fonction de l'insertion order sélectionné
  const filteredData = useMemo(() => {
    if (!campaignData || !campaignData.data) return [];

    if (selectedInsertionOrder === "all") {
      return campaignData.data;
    }

    return campaignData.data.filter(
      (item) => item.insertionOrderId === selectedInsertionOrder
    );
  }, [campaignData, selectedInsertionOrder]);

  const handleKPISelect = (kpi) => {
    setSelectedKPI(kpi);
  };

  const handleFilterChange = (filter) => {
    setSelectedFilter(filter);
  };

  const handleInsertionOrderChange = (e) => {
    setSelectedInsertionOrder(e.target.value);
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(shareableUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const refreshData = async () => {
    try {
      setRefreshing(true);
      setError("");

      // Vérifier si le rapport est lié à Xandr
      if (
        campaignData &&
        campaignData.source === "xandr" &&
        campaignData.xandrCampaignId
      ) {
        const response = await axios.get(
          `${apiUrl}/api/refresh-xandr-data/${dataId}`
        );

        if (response.data.success) {
          setCampaignData(response.data.data);

          // Rafraîchir également les recommandations
          const recommendationsResponse = await axios.get(
            `${apiUrl}/api/recommendations/${dataId}`
          );
          setRecommendations(recommendationsResponse.data.recommendations);
        } else {
          setError("Erreur lors du rafraîchissement des données");
        }
      } else {
        setError("Ce rapport n'est pas lié à une campagne Xandr");
      }
    } catch (err) {
      setError(
        "Erreur lors du rafraîchissement des données: " +
          (err.response?.data?.error || err.message)
      );
    } finally {
      setRefreshing(false);
    }
  };

  const goBack = () => {
    navigate("/");
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("fr-FR", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <LoadingContainer>
        Chargement des données de la campagne...
      </LoadingContainer>
    );
  }

  if (error) {
    return <ErrorContainer>{error}</ErrorContainer>;
  }

  return (
    <DashboardContainer>
      <HeaderContainer>
        <Logo
          src={logo}
          alt="Logo"
          onClick={goBack}
          style={{ cursor: "pointer" }}
        />
        <CampaignTitle>{campaignData.campaignName}</CampaignTitle>
        <HeaderActions>
          <BackButton onClick={goBack}>← Retour</BackButton>
          {campaignData.source === "xandr" && (
            <ActionButton onClick={refreshData} disabled={refreshing}>
              {refreshing ? "Rafraîchissement..." : "↻ Rafraîchir les données"}
            </ActionButton>
          )}
        </HeaderActions>
      </HeaderContainer>
      <ShareContainer>
        <ShareInput
          type="text"
          value={shareableUrl}
          readOnly
          onClick={(e) => e.target.select()}
        />
        <ShareButton onClick={copyToClipboard}>
          {copied ? "Copié !" : "Copier le lien"}
        </ShareButton>
      </ShareContainer>
      {campaignData.source === "xandr" && campaignData.campaignDetails && (
        <CampaignInfo>
          <InfoGrid>
            <InfoItem>
              <InfoLabel>État</InfoLabel>
              <InfoValue>
                {campaignData.campaignDetails.state || "N/A"}
              </InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Date de début</InfoLabel>
              <InfoValue>
                {formatDate(campaignData.campaignDetails.start_date)}
              </InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Date de fin</InfoLabel>
              <InfoValue>
                {formatDate(campaignData.campaignDetails.end_date)}
              </InfoValue>
            </InfoItem>
            {campaignData.campaignDetails.budget && (
              <InfoItem>
                <InfoLabel>Budget</InfoLabel>
                <InfoValue>{campaignData.campaignDetails.budget} €</InfoValue>
              </InfoItem>
            )}
          </InfoGrid>
          {campaignData.lastUpdated && (
            <LastUpdated>
              Dernière mise à jour: {formatDate(campaignData.lastUpdated)}
            </LastUpdated>
          )}
        </CampaignInfo>
      )}
      {insertionOrders.length > 0 && (
        <FiltersContainer>
          <FilterLabel>
            Filtrer par Insertion Order
            <FilterSelect
              value={selectedInsertionOrder}
              onChange={handleInsertionOrderChange}
            >
              <option value="all">Tous les Insertion Orders</option>
              {insertionOrders.map((order) => (
                <option key={order.id} value={order.id}>
                  {order.name}
                </option>
              ))}
            </FilterSelect>
          </FilterLabel>
        </FiltersContainer>
      )}

      <KPIBubbles
        data={filteredData}
        onKPISelect={handleKPISelect}
        selectedKPI={selectedKPI}
      />
      <DataVisualizer
        data={filteredData}
        selectedKPI={selectedKPI}
        selectedFilter={selectedFilter}
        onFilterChange={handleFilterChange}
      />
      <CreativeGallery data={filteredData} selectedKPI={selectedKPI} />
      <Recommendations recommendations={recommendations} />
    </DashboardContainer>
  );
}

export default CampaignDashboard;
